//@ts-ignore
import { formatFloatAsCurrencySymbol } from '../common/helpers.mjs'
//@ts-ignore
import { currencyObject } from '../common/types/types'

interface InvestmentSummaryCardProps {
   getString: (key: string, fallback?: string) => string
   differenceQuote: boolean
   currencyObject: currencyObject
   currencyObjectUSD: currencyObject
   isForeignCurrencyQuote: boolean
   payrollUsdMonthlyTotal: number
   payrollUsdOneTimeTotal: number
   payrollUsdPreviousMonthlyTotal: number
   payrollUsdMonthlyDifference: number
   payrollUsdPreDiscountTotal: number
   payrollUsdFirstYearSavings: number
   payrollUsdFirstYearTotal: number
   previousMonthlyTotal: number
   previousAnnualTotal: number
   newMonthlyTotal: number
   newAnnualTotal: number
   monthlyDifference: number
   annualDifference: number
   oneTimeFee: number
   listPriceTotal: number
   totalDiscount: number
   firstYearTotal: number
}

export default function InvestmentSummaryCard({
   getString,
   differenceQuote,
   currencyObject,
   currencyObjectUSD,
   isForeignCurrencyQuote,
   payrollUsdMonthlyTotal,
   payrollUsdOneTimeTotal,
   payrollUsdPreviousMonthlyTotal,
   payrollUsdMonthlyDifference,
   payrollUsdPreDiscountTotal,
   payrollUsdFirstYearSavings,
   payrollUsdFirstYearTotal,
   previousMonthlyTotal,
   previousAnnualTotal,
   newMonthlyTotal,
   newAnnualTotal,
   monthlyDifference,
   annualDifference,
   oneTimeFee,
   listPriceTotal,
   totalDiscount,
   firstYearTotal,
}: InvestmentSummaryCardProps) {
   const headerLabel = getString(
      'pdf.frontpage.investmentSummary',
      'Investment Summary',
   )
   const oneTimeLabel = getString(
      'pdf.frontpage.oneTimeCostsImplementation',
      'One-time costs (implementation)',
   )
   const preDiscountLabel = getString(
      'pdf.frontpage.preDiscountTotal',
      'Pre-discount total:',
   )

   // managedPayrollServices can be selected with no payroll EE count, so amount
   // checks are used here rather than HasPayrollSelected, which would miss a
   // states-only USD charge.
   const showPayrollUsd =
      isForeignCurrencyQuote &&
      (payrollUsdMonthlyTotal > 0 || payrollUsdOneTimeTotal > 0)

   return (
      <div className='investmentSummaryCard'>
         <div className='investmentCardHeader'>{headerLabel}</div>
         <div className='investmentCardBody'>
            {differenceQuote ? (
               <ExpansionBody
                  getString={getString}
                  currencyObject={currencyObject}
                  previousMonthlyTotal={previousMonthlyTotal}
                  previousAnnualTotal={previousAnnualTotal}
                  newMonthlyTotal={newMonthlyTotal}
                  newAnnualTotal={newAnnualTotal}
                  monthlyDifference={monthlyDifference}
                  annualDifference={annualDifference}
                  oneTimeFee={oneTimeFee}
                  oneTimeLabel={oneTimeLabel}
                  appendCurrencyCode={showPayrollUsd}
               />
            ) : (
               <AcquisitionBody
                  getString={getString}
                  currencyObject={currencyObject}
                  newMonthlyTotal={newMonthlyTotal}
                  oneTimeFee={oneTimeFee}
                  firstYearTotal={firstYearTotal}
                  oneTimeLabel={oneTimeLabel}
                  appendCurrencyCode={showPayrollUsd}
               />
            )}
            <div className='investmentCardFooter'>
               <div className='preDiscountTotal'>
                  <span className='preDiscountLabel'>{preDiscountLabel}</span>{' '}
                  <span className='preDiscountValue'>
                     {formatFloatAsCurrencySymbol(
                        listPriceTotal,
                        currencyObject,
                     )}
                  </span>
               </div>
               {totalDiscount > 0 && (
                  <div className='projectedSavings'>
                     <span className='projectedSavingsLabel'>
                        {differenceQuote
                           ? getString(
                                'pdf.frontpage.projected12MonthSavings',
                                'Projected 12-Month Savings:',
                             )
                           : getString(
                                'pdf.frontpage.projectedFirstYearSavings',
                                'Projected First-Year Savings:',
                             )}
                     </span>{' '}
                     <span className='projectedSavingsValue'>
                        {formatFloatAsCurrencySymbol(
                           totalDiscount,
                           currencyObject,
                        )}
                     </span>
                  </div>
               )}
            </div>
            {showPayrollUsd && (
               <div className='payrollUsdSection'>
                  <div className='payrollUsdHeader'>
                     {getString(
                        'pdf.frontpage.payrollUsdHeader',
                        'Payroll (billed in USD)',
                     )}
                  </div>
                  {differenceQuote ? (
                     payrollUsdMonthlyTotal > 0 && (
                        <CostArrowRow
                           previousLabel={`${getString(
                              'pdf.frontpage.payrollUsdPreviousMonthlyCost',
                              'Previous monthly payroll cost',
                           )} (${currencyObjectUSD.Code})`}
                           previousValue={formatFloatAsCurrencySymbol(
                              payrollUsdPreviousMonthlyTotal,
                              currencyObjectUSD,
                              true,
                           )}
                           newLabel={`${getString(
                              'pdf.frontpage.payrollUsdMonthlyCost',
                              'Estimated monthly payroll cost*',
                           )} (${currencyObjectUSD.Code})`}
                           newValue={formatFloatAsCurrencySymbol(
                              payrollUsdMonthlyTotal,
                              currencyObjectUSD,
                              true,
                           )}
                           differenceLabel={getString(
                              'pdf.frontpage.difference',
                              'Difference',
                           )}
                           differenceValue={formatFloatAsCurrencySymbol(
                              payrollUsdMonthlyDifference,
                              currencyObjectUSD,
                              true,
                           )}
                        />
                     )
                  ) : (
                     <div className='investmentRow acquisitionTwoColRow'>
                        {payrollUsdMonthlyTotal > 0 && (
                           <div className='investmentCell'>
                              <div className='investmentLabel'>
                                 {getString(
                                    'pdf.frontpage.payrollUsdMonthlyCost',
                                    'Estimated monthly payroll cost*',
                                 )}{' '}
                                 ({currencyObjectUSD.Code})
                              </div>
                              <div className='investmentValue payrollUsdValue'>
                                 {formatFloatAsCurrencySymbol(
                                    payrollUsdMonthlyTotal,
                                    currencyObjectUSD,
                                 )}
                              </div>
                           </div>
                        )}
                        <div className='investmentCell'>
                           <div className='investmentLabel'>
                              {getString(
                                 'pdf.frontpage.payrollUsdFirstYearTotal',
                                 'Total estimated first-year payroll cost',
                              )}{' '}
                              ({currencyObjectUSD.Code})
                           </div>
                           <div className='investmentValue'>
                              {formatFloatAsCurrencySymbol(
                                 payrollUsdFirstYearTotal,
                                 currencyObjectUSD,
                              )}
                           </div>
                        </div>
                     </div>
                  )}
                  {payrollUsdOneTimeTotal > 0 && (
                     <div className='investmentRow'>
                        <div className='investmentCell'>
                           <div className='investmentLabel'>
                              {getString(
                                 'pdf.frontpage.payrollUsdOneTimeCosts',
                                 'One-time payroll costs',
                              )}{' '}
                              ({currencyObjectUSD.Code})
                           </div>
                           <div className='investmentValue oneTimeValue'>
                              {formatFloatAsCurrencySymbol(
                                 payrollUsdOneTimeTotal,
                                 currencyObjectUSD,
                              )}
                           </div>
                        </div>
                     </div>
                  )}
                  <div className='investmentCardFooter'>
                     <div className='preDiscountTotal'>
                        <span className='preDiscountLabel'>
                           {getString(
                              'pdf.frontpage.payrollUsdPreDiscountTotal',
                              'Payroll pre-discount total:',
                           )}
                        </span>{' '}
                        <span className='preDiscountValue'>
                           {formatFloatAsCurrencySymbol(
                              payrollUsdPreDiscountTotal,
                              currencyObjectUSD,
                           )}
                        </span>
                     </div>
                     {payrollUsdFirstYearSavings > 0 && (
                        <div className='projectedSavings'>
                           <span className='projectedSavingsLabel'>
                              {getString(
                                 'pdf.frontpage.payrollUsdFirstYearSavings',
                                 'Projected First-Year Payroll Savings:',
                              )}
                           </span>{' '}
                           <span className='projectedSavingsValue'>
                              {formatFloatAsCurrencySymbol(
                                 payrollUsdFirstYearSavings,
                                 currencyObjectUSD,
                              )}
                           </span>
                        </div>
                     )}
                  </div>
               </div>
            )}
         </div>
      </div>
   )
}

function ExpansionBody({
   getString,
   currencyObject,
   previousMonthlyTotal,
   previousAnnualTotal,
   newMonthlyTotal,
   newAnnualTotal,
   monthlyDifference,
   annualDifference,
   oneTimeFee,
   oneTimeLabel,
   appendCurrencyCode,
}: {
   getString: (key: string, fallback?: string) => string
   currencyObject: currencyObject
   previousMonthlyTotal: number
   previousAnnualTotal: number
   newMonthlyTotal: number
   newAnnualTotal: number
   monthlyDifference: number
   annualDifference: number
   oneTimeFee: number
   oneTimeLabel: string
   appendCurrencyCode: boolean
}) {
   const withCode = (label: string) =>
      appendCurrencyCode ? `${label} (${currencyObject.Code})` : label

   const previousMonthlyLabel = getString(
      'pdf.frontpage.previousMonthlyCost',
      'Previous monthly cost',
   )
   const newMonthlyLabel = withCode(
      getString('pdf.frontpage.newMonthlyCost', 'New monthly cost*'),
   )
   const previousAnnualLabel = getString(
      'pdf.frontpage.previousAnnualCost',
      'Previous annual cost',
   )
   const newAnnualLabel = withCode(
      getString('pdf.frontpage.newAnnualCost', 'New annual cost*'),
   )
   const differenceLabel = getString('pdf.frontpage.difference', 'Difference')

   return (
      <>
         <CostArrowRow
            previousLabel={previousMonthlyLabel}
            previousValue={formatFloatAsCurrencySymbol(
               previousMonthlyTotal,
               currencyObject,
               true,
            )}
            newLabel={newMonthlyLabel}
            newValue={formatFloatAsCurrencySymbol(
               newMonthlyTotal,
               currencyObject,
               true,
            )}
            differenceLabel={differenceLabel}
            differenceValue={formatFloatAsCurrencySymbol(
               monthlyDifference,
               currencyObject,
               true,
            )}
         />
         <CostArrowRow
            previousLabel={previousAnnualLabel}
            previousValue={formatFloatAsCurrencySymbol(
               previousAnnualTotal,
               currencyObject,
               true,
            )}
            newLabel={newAnnualLabel}
            newValue={formatFloatAsCurrencySymbol(
               newAnnualTotal,
               currencyObject,
               true,
            )}
            differenceLabel={differenceLabel}
            differenceValue={formatFloatAsCurrencySymbol(
               annualDifference,
               currencyObject,
               true,
            )}
         />
         {oneTimeFee > 0 && (
            <div className='investmentRow'>
               <div className='investmentCell'>
                  <div className='investmentLabel'>
                     {withCode(oneTimeLabel)}
                  </div>
                  <div className='investmentValue oneTimeValue'>
                     {formatFloatAsCurrencySymbol(oneTimeFee, currencyObject)}
                  </div>
               </div>
            </div>
         )}
      </>
   )
}

function AcquisitionBody({
   getString,
   currencyObject,
   newMonthlyTotal,
   oneTimeFee,
   firstYearTotal,
   oneTimeLabel,
   appendCurrencyCode,
}: {
   getString: (key: string, fallback?: string) => string
   currencyObject: currencyObject
   newMonthlyTotal: number
   oneTimeFee: number
   firstYearTotal: number
   oneTimeLabel: string
   appendCurrencyCode: boolean
}) {
   const withCode = (label: string) =>
      appendCurrencyCode ? `${label} (${currencyObject.Code})` : label

   const estimatedMonthlyLabel = withCode(
      getString(
         'pdf.frontpage.estimatedMonthlyCost',
         'Estimated monthly cost*',
      ),
   )
   const firstYearLabel = withCode(
      getString(
         'pdf.frontpage.totalEstimatedFirstYearCost',
         'Total estimated first-year cost',
      ),
   )

   return (
      <>
         <div className='investmentRow'>
            <div className='investmentCell'>
               <div className='investmentLabel'>{estimatedMonthlyLabel}</div>
               <div className='investmentValue'>
                  {formatFloatAsCurrencySymbol(newMonthlyTotal, currencyObject)}
               </div>
            </div>
         </div>
         <div className='investmentRow acquisitionTwoColRow'>
            {oneTimeFee > 0 && (
               <div className='investmentCell'>
                  <div className='investmentLabel'>
                     {withCode(oneTimeLabel)}
                  </div>
                  <div className='investmentValue oneTimeValue'>
                     {formatFloatAsCurrencySymbol(oneTimeFee, currencyObject)}
                  </div>
               </div>
            )}
            <div className='investmentCell'>
               <div className='investmentLabel'>{firstYearLabel}</div>
               <div className='investmentValue'>
                  {formatFloatAsCurrencySymbol(firstYearTotal, currencyObject)}
               </div>
            </div>
         </div>
      </>
   )
}

function CostArrow() {
   return (
      <svg
         xmlns='http://www.w3.org/2000/svg'
         width='32'
         height='13'
         viewBox='0 0 20 8'
         fill='none'
         className='costArrow'
      >
         <path
            d='M0.5 3.18164C0.223858 3.18164 2.98023e-08 3.4055 2.98023e-08 3.68164C2.98023e-08 3.95778 0.223858 4.18164 0.5 4.18164V3.68164V3.18164ZM19.3536 4.03519C19.5488 3.83993 19.5488 3.52335 19.3536 3.32809L16.1716 0.146107C15.9763 -0.0491552 15.6597 -0.0491552 15.4645 0.146107C15.2692 0.341369 15.2692 0.657952 15.4645 0.853214L18.2929 3.68164L15.4645 6.51007C15.2692 6.70533 15.2692 7.02191 15.4645 7.21717C15.6597 7.41244 15.9763 7.41244 16.1716 7.21717L19.3536 4.03519ZM0.5 3.68164V4.18164H19V3.68164V3.18164H0.5V3.68164Z'
            fill='#C6C2BF'
         />
      </svg>
   )
}

function CostArrowRow({
   previousLabel,
   previousValue,
   newLabel,
   newValue,
   differenceLabel,
   differenceValue,
}: {
   previousLabel: string
   previousValue: string
   newLabel: string
   newValue: string
   differenceLabel: string
   differenceValue: string
}) {
   return (
      <div className='investmentRow costArrowRow'>
         <div className='investmentCell previousCell'>
            <div className='investmentLabel'>{previousLabel}</div>
            <div className='investmentValue previousValue'>{previousValue}</div>
         </div>
         <CostArrow />
         <div className='investmentCell'>
            <div className='investmentLabel'>{newLabel}</div>
            <div className='investmentValue'>{newValue}</div>
         </div>
         <CostArrow />
         <div className='investmentCell'>
            <div className='investmentLabel'>{differenceLabel}</div>
            <div className='investmentValue'>{differenceValue}</div>
         </div>
      </div>
   )
}
